import { useEffect, useMemo, useState } from 'react';
import ApiDatabase from 'server';
import UseContext from 'hooks/useContext';
import Modalheader from 'components/modal/components/header';
import ModalBody from 'components/modal/components/body';
import Button from 'components/button';
import PerimeterAccessAlert from 'components/modal/dataModal/data/perimeter/PerimeterAccessAlert';
import PerimeterModalFooter from 'components/modal/dataModal/data/perimeter/PerimeterModalFooter';
import usePerimeterModal from 'components/modal/dataModal/data/perimeter/usePerimeterModal';
import { ARCHIVE_ERROR_OVERRIDES } from 'components/perimeter/perimeterErrors';
import { NO_PERIMETER_TARGET } from 'components/perimeter/perimeterUsage';
import {
  fillText,
  getCountText,
  perimeterText,
} from 'components/perimeter/perimeterText';

// Active missions or members are attached: they move to another perimeter, or
// to none, before the perimeter is archived.
const TransferArchivePerimeterModal = () => {
  const { language } = UseContext();
  const t = (key) => perimeterText(language, key);
  const { data, isSaving, setIsSaving, close, succeed, fail } =
    usePerimeterModal();
  const { idCompany, perimeter, usage, perimeters } = data;
  const perimeterId = perimeter?.id;
  const noPerimeterLabel = t('archive.noPerimeter');

  const options = useMemo(
    () => [
      ...(perimeters || [])
        .filter((other) => other.id !== perimeterId)
        .map((other) => ({ value: other.id, label: other.name })),
      { value: NO_PERIMETER_TARGET, label: noPerimeterLabel },
    ],
    [perimeters, perimeterId, noPerimeterLabel],
  );
  const [target, setTarget] = useState(options[0].value);
  // Members left without perimeter, per destination. The usage opened with
  // the "none" destination; the others are asked when first picked.
  const [leftByTarget, setLeftByTarget] = useState({
    [NO_PERIMETER_TARGET]: Number(usage?.membersLeftWithoutPerimeter) || 0,
  });
  // Archiving stays disabled until the answer for the chosen destination is in.
  const [hasUsageFailed, setHasUsageFailed] = useState(false);
  const [usageAttempt, setUsageAttempt] = useState(0);

  useEffect(() => {
    setHasUsageFailed(false);
    if (target in leftByTarget) return undefined;
    let cancelled = false;
    ApiDatabase.getAdminCompanyPerimeterUsage(
      { idCompany, id: perimeterId, transferTo: target },
      (res) => {
        if (cancelled) return;
        setLeftByTarget((known) => ({
          ...known,
          [target]: Number(res?.data?.membersLeftWithoutPerimeter) || 0,
        }));
      },
      (err) => {
        if (cancelled) return;
        console.error(err);
        setHasUsageFailed(true);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [target, leftByTarget, idCompany, perimeterId, usageAttempt]);

  const isUsageKnown = target in leftByTarget;
  const membersLeft = leftByTarget[target] || 0;
  // The archived perimeter's own name for the "none" toast, else the target's.
  const targetName =
    target === NO_PERIMETER_TARGET
      ? perimeter?.name
      : options.find((option) => option.value === target)?.label;

  const handleConfirm = () => {
    if (isSaving || !isUsageKnown) return;
    setIsSaving(true);
    ApiDatabase.postArchiveAdminCompanyPerimeter(
      {
        idCompany,
        id: perimeterId,
        transferTo: target === NO_PERIMETER_TARGET ? null : target,
      },
      (res) => {
        const transferred = Number(res?.data?.transferredMissions) || 0;
        succeed(
          transferred > 0
            ? getCountText(
                language,
                target === NO_PERIMETER_TARGET
                  ? 'toast.archivedNoPerimeter'
                  : 'toast.transferred',
                transferred,
                { name: targetName },
              )
            : t('toast.archived'),
        );
      },
      (err) => fail(err, { overrides: ARCHIVE_ERROR_OVERRIDES }),
    );
  };

  return (
    <>
      <Modalheader hide={close} hideShow={true}>
        {fillText(t('archive.transferTitle'), { name: perimeter?.name })}
      </Modalheader>
      <ModalBody>
        <div className={'flex flex-col gap-4'}>
          <p className={'text-sm text-gray-600'}>
            {fillText(t('archive.transferIntro'), {
              missions: getCountText(
                language,
                'archive.activeMissions',
                Number(usage?.activeMissions) || 0,
              ),
              members: getCountText(
                language,
                'list.members',
                Number(usage?.members) || 0,
              ),
            })}
          </p>
          <div className={'flex flex-col gap-1'}>
            <label
              htmlFor={'perimeter-transfer-target'}
              className={'text-sm text-gray-700'}
            >
              {t('archive.transferLabel')}
            </label>
            <select
              id={'perimeter-transfer-target'}
              value={target}
              onChange={(event) => setTarget(event.target.value)}
              disabled={isSaving}
              className={
                'h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-yellow-500 focus:outline-none'
              }
            >
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {membersLeft > 0 ? (
            <PerimeterAccessAlert count={membersLeft} />
          ) : null}
          {hasUsageFailed ? (
            <div className={'flex flex-col items-start gap-3'}>
              <p className={'text-sm text-red-500'}>
                {t('archive.usageError')}
              </p>
              <Button
                color={'SECONDARY'}
                size={'SM'}
                onClick={() => setUsageAttempt((count) => count + 1)}
              >
                {t('list.retry')}
              </Button>
            </div>
          ) : null}
        </div>
      </ModalBody>
      <PerimeterModalFooter
        confirmLabel={t('archive.confirm')}
        savingLabel={t('archive.archiving')}
        isSaving={isSaving}
        canConfirm={isUsageKnown}
        onCancel={close}
        onConfirm={handleConfirm}
      />
    </>
  );
};

export default TransferArchivePerimeterModal;
